import { ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebase';

export const getEmailAnalytics = async (timeRange = '30days') => {
  const trackingRef = ref(database, 'emailTracking');
  const snapshot = await get(trackingRef);
  
  if (!snapshot.exists()) {
    return {
      totalEmailsSent: 0,
      emailTypes: {},
      deliveryRates: {},
      openRates: {},
      clickRates: {},
      recentEvents: [],
    };
  }

  const events = Object.values(snapshot.val());
  const now = new Date();
  let startDate;

  switch (timeRange) {
    case '7days':
      startDate = new Date(now.setDate(now.getDate() - 7));
      break;
    case '30days':
      startDate = new Date(now.setDate(now.getDate() - 30));
      break;
    case '90days':
      startDate = new Date(now.setDate(now.getDate() - 90));
      break;
    default:
      startDate = new Date(0);
  }

  const filteredEvents = events.filter(event => 
    new Date(event.timestamp) >= startDate
  );

  const analytics = {
    totalEmailsSent: filteredEvents.filter(event => event.eventType === 'email_sent').length,
    emailTypes: {},
    deliveryRates: {},
    openRates: {},
    clickRates: {},
    recentEvents: filteredEvents
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10),
  };

  // Calculate email type distribution
  filteredEvents.forEach(event => {
    if (event.eventType === 'email_sent') {
      const emailType = event.metadata.emailType || 'unknown';
      analytics.emailTypes[emailType] = (analytics.emailTypes[emailType] || 0) + 1;
    }
  });

  // Calculate delivery rates
  const sentEmails = filteredEvents.filter(event => event.eventType === 'email_sent');
  const deliveredEmails = filteredEvents.filter(event => event.eventType === 'email_delivered');
  
  Object.keys(analytics.emailTypes).forEach(emailType => {
    const typeSent = sentEmails.filter(event => event.metadata.emailType === emailType).length;
    const typeDelivered = deliveredEmails.filter(event => event.metadata.emailType === emailType).length;
    
    analytics.deliveryRates[emailType] = typeSent > 0 
      ? (typeDelivered / typeSent) * 100 
      : 0;
  });

  // Calculate open rates
  const openedEmails = filteredEvents.filter(event => event.eventType === 'email_opened');
  
  Object.keys(analytics.emailTypes).forEach(emailType => {
    const typeSent = sentEmails.filter(event => event.metadata.emailType === emailType).length;
    const typeOpened = openedEmails.filter(event => event.metadata.emailType === emailType).length;
    
    analytics.openRates[emailType] = typeSent > 0 
      ? (typeOpened / typeSent) * 100 
      : 0;
  });

  // Calculate click rates
  const clickedEmails = filteredEvents.filter(event => event.eventType === 'link_clicked');
  
  Object.keys(analytics.emailTypes).forEach(emailType => {
    const typeSent = sentEmails.filter(event => event.metadata.emailType === emailType).length;
    const typeClicked = clickedEmails.filter(event => event.metadata.emailType === emailType).length;
    
    analytics.clickRates[emailType] = typeSent > 0 
      ? (typeClicked / typeSent) * 100 
      : 0;
  });

  return analytics;
};

export const getEmailTrends = async (days = 30) => {
  const trackingRef = ref(database, 'emailTracking');
  const snapshot = await get(trackingRef);
  
  if (!snapshot.exists()) {
    return [];
  }

  const events = Object.values(snapshot.val());
  const now = new Date();
  const startDate = new Date(now.setDate(now.getDate() - days));
  
  const trends = [];
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    const dayEvents = events.filter(event => 
      new Date(event.timestamp).toDateString() === date.toDateString()
    );

    trends.push({
      date: date.toISOString().split('T')[0],
      sent: dayEvents.filter(event => event.eventType === 'email_sent').length,
      delivered: dayEvents.filter(event => event.eventType === 'email_delivered').length,
      opened: dayEvents.filter(event => event.eventType === 'email_opened').length,
      clicked: dayEvents.filter(event => event.eventType === 'link_clicked').length,
    });
  }

  return trends;
};

export const getEmailPerformance = async () => {
  const trackingRef = ref(database, 'emailTracking');
  const snapshot = await get(trackingRef);
  
  if (!snapshot.exists()) {
    return [];
  }

  const events = Object.values(snapshot.val());
  const emailTypes = [...new Set(events
    .filter(event => event.eventType === 'email_sent')
    .map(event => event.metadata.emailType || 'unknown')
  )];

  return emailTypes.map(emailType => {
    const typeEvents = events.filter(event => event.metadata.emailType === emailType);
    const sent = typeEvents.filter(event => event.eventType === 'email_sent').length;
    const delivered = typeEvents.filter(event => event.eventType === 'email_delivered').length;
    const opened = typeEvents.filter(event => event.eventType === 'email_opened').length;
    const clicked = typeEvents.filter(event => event.eventType === 'link_clicked').length;

    return {
      emailType,
      sent,
      delivered,
      opened,
      clicked,
      deliveryRate: sent > 0 ? (delivered / sent) * 100 : 0,
      openRate: sent > 0 ? (opened / sent) * 100 : 0,
      clickRate: sent > 0 ? (clicked / sent) * 100 : 0,
    };
  });
}; 